import type { TypedSupabaseClient } from '../client';

interface LowStockArticleRow {
  id: string;
  code: string;
  name: string;
  stock_min: number | null;
  unit: string | null;
}

interface StockMovementRow {
  article_id: string;
  movement_type: string;
  quantity: number;
}

function formatQty(value: number): string {
  return value.toLocaleString('fr-TN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
}

function aggregateStock(rows: StockMovementRow[]): Map<string, number> {
  const stock = new Map<string, number>();

  for (const row of rows) {
    const current = stock.get(row.article_id) ?? 0;
    const quantity = Number(row.quantity ?? 0);
    if (row.movement_type === 'in') {
      stock.set(row.article_id, current + quantity);
    } else if (row.movement_type === 'out') {
      stock.set(row.article_id, current - quantity);
    } else {
      stock.set(row.article_id, current + quantity);
    }
  }

  return stock;
}

export async function ensureLowStockNotificationsForArticles(
  supabase: TypedSupabaseClient,
  articleIds?: string[],
): Promise<number> {
  const scopedArticleIds = [...new Set((articleIds ?? []).filter(Boolean))];

  let articleQuery = supabase
    .from('articles')
    .select('id, code, name, stock_min, unit')
    .eq('is_active', true)
    .gt('stock_min', 0);

  if (scopedArticleIds.length > 0) {
    articleQuery = articleQuery.in('id', scopedArticleIds);
  }

  const { data: articles, error: articleError } = await articleQuery;
  if (articleError) throw articleError;

  const candidates = (articles ?? []) as LowStockArticleRow[];
  if (candidates.length === 0) return 0;

  const candidateIds = candidates.map((article) => article.id);
  const { data: movements, error: movementError } = await supabase
    .from('stock_movements')
    .select('article_id, movement_type, quantity')
    .in('article_id', candidateIds);
  if (movementError) throw movementError;

  const stockByArticle = aggregateStock((movements ?? []) as StockMovementRow[]);
  const lowStockArticles = candidates
    .map((article) => ({
      article,
      currentStock: stockByArticle.get(article.id) ?? 0,
      stockMin: Number(article.stock_min ?? 0),
    }))
    .filter(({ currentStock, stockMin }) => currentStock <= stockMin);

  if (lowStockArticles.length === 0) return 0;

  const lowStockIds = lowStockArticles.map(({ article }) => article.id);
  const { data: existing, error: existingError } = await supabase
    .from('admin_notifications')
    .select('entity_id')
    .eq('kind', 'product_low_stock')
    .eq('entity_type', 'article')
    .in('entity_id', lowStockIds);
  if (existingError) throw existingError;

  const alreadyNotified = new Set((existing ?? []).map((row) => row.entity_id).filter(Boolean));
  const rowsToInsert = lowStockArticles
    .filter(({ article }) => !alreadyNotified.has(article.id))
    .map(({ article, currentStock, stockMin }) => {
      const unit = article.unit ?? '';
      const currentText = `${formatQty(currentStock)}${unit ? ` ${unit}` : ''}`;
      const minText = `${formatQty(stockMin)}${unit ? ` ${unit}` : ''}`;
      return {
        kind: 'product_low_stock',
        title: `Stock minimum atteint: ${article.name}`,
        message: `Stock actuel: ${currentText}. Minimum: ${minText}.`,
        link_path: `/articles/${article.id}`,
        entity_type: 'article',
        entity_id: article.id,
        metadata: {
          articleId: article.id,
          articleCode: article.code,
          currentStock,
          stockMin,
          unit,
        },
      };
    });

  if (rowsToInsert.length === 0) return 0;

  const { data: inserted, error: insertError } = await supabase
    .from('admin_notifications')
    .insert(rowsToInsert)
    .select('id');
  if (insertError) {
    if (isUniqueViolation(insertError)) return 0;
    throw insertError;
  }

  return inserted?.length ?? rowsToInsert.length;
}

function isUniqueViolation(error: unknown): boolean {
  return typeof error === 'object'
    && error !== null
    && 'code' in error
    && (error as { code?: string }).code === '23505';
}
